/**
 * ImageLightbox — full-screen viewer for diagram node images.
 * Iter Feb 2026 R47.
 *
 * Renders nothing when `src` is falsy. Click backdrop / press ESC / click ✕
 * to close. Uses a plain <img> and portal-free absolute positioning to work
 * both inside the authenticated editor and the anonymous PublicDiagramView.
 */
import { useEffect } from "react";
import { X } from "lucide-react";

export default function ImageLightbox({ src, alt, onClose }) {
  useEffect(() => {
    if (!src) return;
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [src, onClose]);

  if (!src) return null;
  return (
    <div
      className="fixed inset-0 z-[200] bg-black/80 flex items-center justify-center p-6 cursor-zoom-out"
      onClick={onClose}
      data-testid="diagram-image-lightbox"
    >
      <button
        onClick={onClose}
        className="absolute top-4 right-4 text-white/80 hover:text-white p-2"
        data-testid="lightbox-close"
        aria-label="Close"
      >
        <X className="w-6 h-6" />
      </button>
      <img
        src={src}
        alt={alt || ""}
        className="max-w-[95vw] max-h-[90vh] object-contain shadow-2xl border border-white/10"
        onClick={(e) => e.stopPropagation()}
        draggable={false}
        data-testid="lightbox-image"
      />
      {alt ? (
        <div
          className="absolute bottom-6 left-1/2 -translate-x-1/2 text-white/90 text-sm bg-black/40 px-3 py-1 border border-white/10"
          onClick={(e) => e.stopPropagation()}
        >
          {alt}
        </div>
      ) : null}
    </div>
  );
}
